import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../api';
import GlassCard from './ui/GlassCard';
import Btn from './ui/Btn';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import Header from './ui/Header';
import SearchBar from './ui/SearchBar';
import Icon from './ui/Icon';

interface ShoppingItem {
  id: number;
  title: string;
  note?: string | null;
  is_bought: number;
  created_at?: string;
  bought_at?: string | null;
}

const Checkbox: React.FC<{ checked: boolean; onClick: () => void }> = ({ checked, onClick }) => (
  <button
    onClick={onClick}
    aria-label={checked ? 'Вернуть в список' : 'Отметить купленным'}
    style={{
      width: 28,
      height: 28,
      flexShrink: 0,
      borderRadius: 9,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s',
      background: checked ? 'var(--teal-solid)' : 'transparent',
      border: checked ? '1px solid var(--teal-solid)' : '1.5px solid var(--glass-border-strong)',
    }}
  >
    {checked && <Icon name="check" size={16} color="rgba(0,0,0,0.85)" />}
  </button>
);

const ShoppingSection: React.FC = () => {
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editNote, setEditNote] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const addInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getShoppingItems();
      setItems(res.data);
    } catch (err) {
      console.error('Error loading shopping list', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const displayed = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      i.title.toLowerCase().includes(q) ||
      (i.note || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const pending = useMemo(() => displayed.filter(i => !i.is_bought), [displayed]);
  const bought = useMemo(() => displayed.filter(i => i.is_bought), [displayed]);

  // Счётчик в шапке и очистка смотрят на весь список, а не на найденное.
  const pendingTotal = useMemo(() => items.filter(i => !i.is_bought).length, [items]);
  const boughtTotal = useMemo(() => items.filter(i => i.is_bought).length, [items]);

  const add = async () => {
    const title = draft.trim();
    if (adding) return;
    if (!title) {
      addInputRef.current?.focus();
      return;
    }

    setAdding(true);
    try {
      const res = await api.createShoppingItem({ title });
      setItems(list => [res.data, ...list]);
      setDraft('');
      addInputRef.current?.focus();
    } catch (err) {
      console.error('Add error', err);
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (item: ShoppingItem) => {
    // Оптимистично: на рынке важна мгновенная реакция
    setItems(list => list.map(i =>
      i.id === item.id ? { ...i, is_bought: i.is_bought ? 0 : 1 } : i
    ));
    try {
      await api.toggleShoppingItem(item.id);
    } catch (err) {
      console.error('Toggle error', err);
      load();
    }
  };

  const remove = async (item: ShoppingItem) => {
    setItems(list => list.filter(i => i.id !== item.id));
    try {
      await api.deleteShoppingItem(item.id);
    } catch (err) {
      console.error('Delete error', err);
      load();
    }
  };

  const openEdit = (item: ShoppingItem) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditNote(item.note || '');
  };

  const saveEdit = async () => {
    if (editingId === null) return;
    const title = editTitle.trim();
    if (!title) return;

    const id = editingId;
    const note = editNote.trim();
    setItems(list => list.map(i => i.id === id ? { ...i, title, note } : i));
    setEditingId(null);
    try {
      await api.updateShoppingItem(id, { title, note });
    } catch (err) {
      console.error('Edit error', err);
      load();
    }
  };

  const clearBought = async () => {
    setItems(list => list.filter(i => !i.is_bought));
    setConfirmClear(false);
    try {
      await api.clearBoughtShoppingItems();
    } catch (err) {
      console.error('Clear error', err);
      load();
    }
  };

  const renderRow = (item: ShoppingItem) => {
    const isEditing = editingId === item.id;
    const done = !!item.is_bought;

    if (isEditing) {
      return (
        <GlassCard key={item.id} style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
              placeholder="Что купить"
              autoFocus
            />
            <input
              value={editNote}
              onChange={e => setEditNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
              placeholder="Заметка: количество, корпус, где брать..."
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="primary" icon="check" onClick={saveEdit} style={{ flex: 1, justifyContent: 'center' }}>
                Сохранить
              </Btn>
              <Btn variant="ghost" onClick={() => setEditingId(null)}>Отмена</Btn>
            </div>
          </div>
        </GlassCard>
      );
    }

    return (
      <GlassCard key={item.id} style={{ padding: '12px 14px', opacity: done ? 0.55 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Checkbox checked={done} onClick={() => toggle(item)} />
          <div
            onClick={() => openEdit(item)}
            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
          >
            <div style={{
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text0)',
              textDecoration: done ? 'line-through' : 'none',
              wordBreak: 'break-word',
            }}>
              {item.title}
            </div>
            {item.note && (
              <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3, wordBreak: 'break-word' }}>
                {item.note}
              </div>
            )}
          </div>
          <button
            onClick={() => remove(item)}
            aria-label="Удалить"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 6,
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <Icon name="trash" size={17} color="var(--text3)" />
          </button>
        </div>
      </GlassCard>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Покупки" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
          Загрузка...
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {confirmClear && (
        <ConfirmDialog
          message={`Убрать купленное из списка (${boughtTotal})?`}
          onConfirm={clearBought}
          onCancel={() => setConfirmClear(false)}
        />
      )}

      <Header
        title="Покупки"
        action={
          pendingTotal > 0 ? (
            <span style={{
              fontFamily: 'var(--mono)',
              fontSize: 13,
              color: 'var(--teal)',
              background: 'var(--teal-dim)',
              border: '1px solid var(--teal-mid)',
              borderRadius: 'var(--r-sm)',
              padding: '5px 11px',
            }}>
              {pendingTotal}
            </span>
          ) : undefined
        }
      />

      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', gap: 8 }}>
        <input
          ref={addInputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="Что купить?"
          style={{ flex: 1 }}
        />
        <Btn variant="primary" icon="plus" onClick={add} disabled={adding}>
          Добавить
        </Btn>
      </div>

      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Название, заметка..." />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {displayed.length === 0 ? (
          <EmptyState
            icon="cart"
            title={search ? 'Ничего не найдено' : 'Список пуст'}
            sub={search ? 'Попробуйте другой запрос' : 'Добавьте, что нужно купить на рынке'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pending.map(renderRow)}

            {bought.length > 0 && (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: pending.length ? 18 : 0,
                  marginBottom: 2,
                  padding: '0 4px',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Куплено · {bought.length}
                  </span>
                  <button
                    onClick={() => setConfirmClear(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 2px',
                      fontFamily: 'var(--sans)',
                      fontSize: 13,
                      color: 'var(--text2)',
                    }}
                  >
                    Очистить
                  </button>
                </div>
                {bought.map(renderRow)}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingSection;
